import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import JsBarcode from "jsbarcode";
import { toast } from "react-toastify";
import "./jobsheetadd.css";

import {
  BsArrowLeft,
  BsBuilding,
  BsChatLeftText,
  BsImages,
  BsPlusLg,
  BsSpeedometer2,
  BsTrash,
  BsTruck,
  BsUpcScan,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";
import SectionCard from "./SectionCard";
import JobPhotosSection from "./JobPhotosSection";

import { serviceCreateScaleJob } from "../../api/serviceApi";
import {
  serviceDeletePhoto,
  serviceUploadPhoto,
} from "../../api/servicePhotoApi";

// ============================================================
// OPTIONS
// ============================================================

const SCALE_TYPES = ["Classic", "Compact", "Advanced"];

const MAKES = [
  "Kenworth",
  "Mack",
  "Western Star",
  "Freightliner",
  "Fuso",
  "Isuzu",
  "Hino",
  "Volvo",
  "Scania",
  "IVECO",
  "MAN",
];

const TRUCK_CONFIGS = ["4x2", "6x4", "8x4", "10x4", "10x6", "Other"];

const AXLE_SENSOR_TYPE = ["Air", "Spring", "Angle"];

const TRAILER_CONFIGS = ["Semi", "Dog", "Pig", "B-Double", "Other"];

// ============================================================
// INITIAL STATE
// ============================================================

const EMPTY_CUSTOMER = {
  customerName: "",
  companyName: "",
  address: "",
};

const EMPTY_SCALE = {
  scaleType: "",
  serialNumber: "",
  serverSerialNumber: "",
  fleetmaster: false,
  iccid: "",
};

const EMPTY_TRUCK = {
  make: "",
  year: "",
  model: "",
  body: "",
  rego: "",
  vin: "",
  truckConfig: "",
  steerSensorType: "",
  driveSensorType: "",
  comments: [],
};

const EMPTY_TRAILER = {
  make: "",
  year: "",
  model: "",
  body: "",
  rego: "",
  vin: "",
  trailerConfig: "",
  trailerASensorType: "",
  trailerBSensorType: "",
  comments: [],
};

// ============================================================
// HELPERS
// ============================================================

function makeBarcodeId() {
  const now = new Date();

  const year = String(now.getFullYear()).slice(-2);

  const month = String(now.getMonth() + 1).padStart(2, "0");

  const day = String(now.getDate()).padStart(2, "0");

  const random = Math.random().toString(36).slice(2, 8).toUpperCase();

  return `HW-${year}${month}${day}${random}`;
}

const makeCommentId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `comment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const cleanComments = (comments) => {
  if (!Array.isArray(comments)) {
    return [];
  }

  return comments
    .map((comment) => {
      if (typeof comment === "string") {
        return comment.trim();
      }

      return String(comment?.text || "").trim();
    })
    .filter(Boolean);
};

// ============================================================
// COMPONENT
// ============================================================

const JobSheetAdd = () => {
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);

  const barcodeCanvasRef = useRef(null);
  const [barcodeValue, setBarcodeValue] = useState(makeBarcodeId());

  const [customerDetails, setCustomerDetails] = useState(EMPTY_CUSTOMER);
  const [scaleDetails, setScaleDetails] = useState(EMPTY_SCALE);
  const [truckDetails, setTruckDetails] = useState(EMPTY_TRUCK);
  const [trailerDetails, setTrailerDetails] = useState(EMPTY_TRAILER);

  const [truckCommentDraft, setTruckCommentDraft] = useState("");
  const [trailerCommentDraft, setTrailerCommentDraft] = useState("");

  //for photos addition
  const [jobImages, setJobImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);

  // ==========================================================
  // BARCODE
  // ==========================================================
  useEffect(() => {
    if (!barcodeCanvasRef.current || !barcodeValue) {
      return;
    }

    try {
      JsBarcode(barcodeCanvasRef.current, barcodeValue, {
        format: "CODE128",
        lineColor: "#000000",
        width: 2,
        height: 52,
        displayValue: true,
        fontSize: 13,
        margin: 8,
      });
    } catch (error) {
      console.error("Barcode render error:", error);
    }
  }, [barcodeValue]);

  // ==========================================================
  // INPUT HANDLER
  // ==========================================================
  const handle = (setter) => (event) => {
    const { name, value, type, checked } = event.target;

    setter((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const regenerateBarcode = () => {
    setBarcodeValue(makeBarcodeId());
  };

  // ==========================================================
  // TRUCK COMMENTS
  // ==========================================================
  const addTruckComment = () => {
    if (submitting) {
      return;
    }

    const text = truckCommentDraft.trim();

    if (!text) {
      return;
    }

    setTruckDetails((previous) => ({
      ...previous,
      comments: [
        ...previous.comments,
        {
          id: makeCommentId(),
          text,
          type: "new",
        },
      ],
    }));

    setTruckCommentDraft("");
  };

  const removeTruckComment = (commentId) => {
    if (submitting) {
      return;
    }

    setTruckDetails((previous) => ({
      ...previous,
      comments: previous.comments.filter((comment) => comment.id !== commentId),
    }));
  };

  // ==========================================================
  // TRAILER COMMENTS
  // ==========================================================
  const addTrailerComment = () => {
    if (submitting) {
      return;
    }

    const text = trailerCommentDraft.trim();

    if (!text) {
      return;
    }

    setTrailerDetails((previous) => ({
      ...previous,
      comments: [
        ...previous.comments,
        {
          id: makeCommentId(),
          text,
          type: "new",
        },
      ],
    }));

    setTrailerCommentDraft("");
  };

  const removeTrailerComment = (commentId) => {
    if (submitting) {
      return;
    }

    setTrailerDetails((previous) => ({
      ...previous,
      comments: previous.comments.filter((comment) => comment.id !== commentId),
    }));
  };

  // ==========================================================
  // VALIDATION
  // ==========================================================
  const isValid = useMemo(() => {
    return Boolean(
      barcodeValue &&
      customerDetails.customerName.trim() &&
      truckDetails.make.trim() &&
      truckDetails.truckConfig.trim() &&
      scaleDetails.scaleType.trim(),
    );
  }, [
    barcodeValue,
    customerDetails.customerName,
    truckDetails.make,
    truckDetails.truckConfig,
    scaleDetails.scaleType,
  ]);

  // ==========================================================
  // SUBMIT
  // ==========================================================
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    if (!isValid) {
      toast.error("Please fill all required fields.");
      return;
    }

    const uploadedThisAttempt = [];
    let jobSaved = false;

    try {
      setSubmitting(true);

      const uploadedJobImages = await uploadJobImages(uploadedThisAttempt);

      const payload = {
        barcode: barcodeValue.trim(),

        customerDetails: {
          customerName: customerDetails.customerName.trim(),
          companyName: customerDetails.companyName.trim(),
          address: customerDetails.address.trim(),
        },

        truckDetails: {
          make: truckDetails.make.trim(),
          year: truckDetails.year || "",
          model: truckDetails.model.trim(),
          body: truckDetails.body.trim(),
          rego: truckDetails.rego.trim(),
          vin: truckDetails.vin.trim(),
          truckConfig: truckDetails.truckConfig.trim(),
          steerSensorType: truckDetails.steerSensorType.trim(),
          driveSensorType: truckDetails.driveSensorType.trim(),
          comments: cleanComments(truckDetails.comments),
        },

        trailerDetails: {
          make: trailerDetails.make.trim(),
          year: trailerDetails.year || "",
          model: trailerDetails.model.trim(),
          body: trailerDetails.body.trim(),
          rego: trailerDetails.rego.trim(),
          vin: trailerDetails.vin.trim(),
          trailerConfig: trailerDetails.trailerConfig.trim(),
          trailerASensorType: trailerDetails.trailerASensorType.trim(),
          trailerBSensorType: trailerDetails.trailerBSensorType.trim(),
          comments: cleanComments(trailerDetails.comments),
        },

        scale: {
          scaleType: scaleDetails.scaleType.trim(),
          serialNumber: scaleDetails.serialNumber.trim(),
          serverSerialNumber: scaleDetails.serverSerialNumber.trim(),
          fleetmaster: scaleDetails.fleetmaster,
          iccid: scaleDetails.iccid.trim(),
        },

        jobImages: uploadedJobImages,
      };

      const createdJob = await serviceCreateScaleJob(payload);

      const jobId = createdJob?.id || createdJob?._id;

      jobSaved = true;
      toast.success("Scale job created successfully.");

      if (jobId) {
        navigate(`/managejobs/view/${jobId}`, { replace: true });
      } else {
        navigate("/managejobs", { replace: true });
      }
    } catch (error) {
      console.error("Error creating scale job:", error);

      if (!jobSaved) {
        await cleanupUploadedPhotos(uploadedThisAttempt);
      }
      toast.error(error?.message || "Failed to create scale job.");
    } finally {
      setUploadProgress(null);
      setSubmitting(false);
    }
  };

  // ==========================================================
  // upload job images
  // ==========================================================
  const uploadJobImages = async (uploadedThisAttempt) => {
    // const localImages = jobImages.filter((image) => image.type === "local");
    const localImages = jobImages.filter(
      (image) =>
        (image.type === "local" || image.type === "camera") &&
        image.file instanceof File,
    );

    if (localImages.length === 0) {
      return [];
    }

    setUploadProgress({
      completed: 0,
      total: localImages.length,
    });

    const uploadedImages = [];

    let completed = 0;

    for (const image of localImages) {
      const safeReference = String(barcodeValue || "job").replace(
        /[^A-Za-z0-9_-]/g,
        "_",
      );

      const filename = [
        safeReference,
        "job",
        Date.now(),
        Math.random().toString(36).slice(2, 8),
      ].join("-");

      const result = await serviceUploadPhoto({
        file: image.file,
        folder: "tims/jobs",
        filename,
      });

      if (!result?.url) {
        throw new Error("Job image upload failed.");
      }

      uploadedThisAttempt.push(result.url);

      uploadedImages.push({
        imageUrl: result.url,
        comment: String(image.comment || "").trim(),
      });

      completed += 1;

      setUploadProgress({
        completed,
        total: localImages.length,
      });
    }

    return uploadedImages;
  };

  const cleanupUploadedPhotos = async (urls) => {
    if (!urls.length) {
      return;
    }

    await Promise.allSettled(urls.map((url) => serviceDeletePhoto(url)));
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div className="job-add-page">
      <Sidebar />

      <main className="job-add-content">
        <div className="job-add-container">
          {/* ================================================
              HEADER
          ================================================= */}

          <header className="job-add-header">
            <button
              type="button"
              className="job-add-back"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              <BsArrowLeft />
              Back
            </button>

            <h1>New Scale Job</h1>

            <p>
              Create a new HaulWeigh scale job and configure the vehicle,
              customer and scale details.
            </p>
          </header>

          {/* ================================================
              MAIN CARD
          ================================================= */}

          <form className="job-add-card" onSubmit={handleSubmit}>
            {/* ==============================================
                LEFT / JOB IDENTITY
            =============================================== */}

            <aside className="job-add-sidebar">
              <div className="job-add-barcode-icon">
                <BsUpcScan />
              </div>

              <div className="job-add-sidebar-title">
                <strong>HaulWeigh Job Barcode</strong>

                <span>This barcode uniquely identifies the scale job.</span>
              </div>

              <div className="job-add-barcode">
                <canvas ref={barcodeCanvasRef} />
              </div>

              <div className="job-add-reference">
                <span>Job Reference</span>

                <strong>{barcodeValue}</strong>
              </div>

              <button
                type="button"
                className="job-btn ghost job-add-regenerate"
                onClick={regenerateBarcode}
                disabled={submitting}
              >
                <BsUpcScan />
                Regenerate Barcode
              </button>

              <div className="job-add-sidebar-note">
                <strong>Required information</strong>

                <span>
                  Customer name, scale type, truck make and truck configuration
                  are required before the job can be created.
                </span>
              </div>
            </aside>

            {/* ==============================================
                FORM
            =============================================== */}

            <div className="job-add-form">
              {/* ============================================
                  CUSTOMER
              ============================================= */}

              <SectionCard
                title="Customer Details"
                description="Customer and company information for this job."
                icon={<BsBuilding />}
                defaultOpen
              >
                <div className="job-grid">
                  <div className="job-field formInput">
                    <label htmlFor="customerName">
                      Customer Name <span className="req">*</span>
                    </label>

                    <input
                      id="customerName"
                      type="text"
                      name="customerName"
                      value={customerDetails.customerName}
                      placeholder="Customer name"
                      onChange={handle(setCustomerDetails)}
                      disabled={submitting}
                      required
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="companyName">Company Name</label>

                    <input
                      id="companyName"
                      type="text"
                      name="companyName"
                      value={customerDetails.companyName}
                      placeholder="Company name"
                      onChange={handle(setCustomerDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput job-full">
                    <label htmlFor="address">Address</label>

                    <input
                      id="address"
                      type="text"
                      name="address"
                      value={customerDetails.address}
                      placeholder="Customer address"
                      onChange={handle(setCustomerDetails)}
                      disabled={submitting}
                    />
                  </div>
                </div>
              </SectionCard>

              {/* ============================================
                  SCALE
              ============================================= */}

              <SectionCard
                title="Scale Details"
                description="Scale hardware and communication details."
                icon={<BsSpeedometer2 />}
                defaultOpen
              >
                <div className="job-grid">
                  <div className="job-field formInput">
                    <label htmlFor="scaleType">
                      Scale Type <span className="req">*</span>
                    </label>

                    <select
                      id="scaleType"
                      name="scaleType"
                      value={scaleDetails.scaleType}
                      onChange={handle(setScaleDetails)}
                      disabled={submitting}
                      required
                    >
                      <option value="">Select scale type...</option>

                      {SCALE_TYPES.map((scaleType) => (
                        <option key={scaleType} value={scaleType}>
                          {scaleType}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="serialNumber">Serial Number</label>

                    <input
                      id="serialNumber"
                      type="text"
                      name="serialNumber"
                      value={scaleDetails.serialNumber}
                      placeholder="Scale serial number"
                      onChange={handle(setScaleDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="serverSerialNumber">
                      Server Serial Number
                    </label>

                    <input
                      id="serverSerialNumber"
                      type="text"
                      name="serverSerialNumber"
                      value={scaleDetails.serverSerialNumber}
                      placeholder="Server serial number"
                      onChange={handle(setScaleDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="iccid">ICCID</label>

                    <input
                      id="iccid"
                      type="text"
                      name="iccid"
                      value={scaleDetails.iccid}
                      placeholder="SIM ICCID"
                      onChange={handle(setScaleDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-full">
                    <label className="job-checkbox">
                      <input
                        type="checkbox"
                        name="fleetmaster"
                        checked={scaleDetails.fleetmaster}
                        onChange={handle(setScaleDetails)}
                        disabled={submitting}
                      />

                      <span className="job-checkbox-control" />

                      <span className="job-checkbox-text">
                        <strong>Fleetmaster</strong>

                        <small>
                          Enable Fleetmaster for this scale installation.
                        </small>
                      </span>
                    </label>
                  </div>
                </div>
              </SectionCard>

              {/* ============================================
                  TRUCK
              ============================================= */}

              <SectionCard
                title="Truck Details"
                description="Primary truck configuration, identification and axle sensors."
                icon={<BsTruck />}
                defaultOpen
              >
                <div className="job-grid">
                  <div className="job-field formInput">
                    <label htmlFor="truckMake">
                      Make <span className="req">*</span>
                    </label>

                    <select
                      id="truckMake"
                      name="make"
                      value={truckDetails.make}
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                      required
                    >
                      <option value="">Select make...</option>

                      {MAKES.map((make) => (
                        <option key={make} value={make}>
                          {make}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="truckYear">Year</label>

                    <input
                      id="truckYear"
                      type="number"
                      name="year"
                      min="1900"
                      step="1"
                      value={truckDetails.year}
                      placeholder="Year"
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="truckModel">Model</label>

                    <input
                      id="truckModel"
                      type="text"
                      name="model"
                      value={truckDetails.model}
                      placeholder="Model"
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="truckBody">Body</label>

                    <input
                      id="truckBody"
                      type="text"
                      name="body"
                      value={truckDetails.body}
                      placeholder="Body"
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="truckRego">Rego</label>

                    <input
                      id="truckRego"
                      type="text"
                      name="rego"
                      value={truckDetails.rego}
                      placeholder="Registration"
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="truckVin">VIN</label>

                    <input
                      id="truckVin"
                      type="text"
                      name="vin"
                      value={truckDetails.vin}
                      placeholder="Vehicle identification number"
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="truckConfig">
                      Truck Config <span className="req">*</span>
                    </label>

                    <select
                      id="truckConfig"
                      name="truckConfig"
                      value={truckDetails.truckConfig}
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                      required
                    >
                      <option value="">Select configuration...</option>

                      {TRUCK_CONFIGS.map((config) => (
                        <option key={config} value={config}>
                          {config}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="steerSensorType">Steer Sensor Type</label>

                    <select
                      id="steerSensorType"
                      name="steerSensorType"
                      value={truckDetails.steerSensorType}
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                    >
                      <option value="">Select sensor type...</option>

                      {AXLE_SENSOR_TYPE.map((axleType) => (
                        <option key={`steer-${axleType}`} value={axleType}>
                          {axleType}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="driveSensorType">Drive Sensor Type</label>

                    <select
                      id="driveSensorType"
                      name="driveSensorType"
                      value={truckDetails.driveSensorType}
                      onChange={handle(setTruckDetails)}
                      disabled={submitting}
                    >
                      <option value="">Select sensor type...</option>

                      {AXLE_SENSOR_TYPE.map((axleType) => (
                        <option key={`drive-${axleType}`} value={axleType}>
                          {axleType}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* ==========================================
                    TRUCK COMMENTS
                =========================================== */}
                <section className="job-add-comments-section">
                  <div className="job-add-comments-header">
                    <div className="job-add-comments-icon">
                      <BsChatLeftText />
                    </div>

                    <div>
                      <h3>Truck Comments</h3>

                      <p>
                        Add notes about the truck configuration or installation
                        requirements.
                      </p>
                    </div>
                  </div>

                  {truckDetails.comments.length > 0 && (
                    <div className="job-add-comments">
                      {truckDetails.comments.map((comment, index) => (
                        <div key={comment.id} className="job-add-comment">
                          <span className="job-add-comment-number">
                            {index + 1}
                          </span>

                          <span className="job-add-comment-text">
                            {comment.text}
                          </span>

                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => removeTruckComment(comment.id)}
                            aria-label={`Remove truck comment ${index + 1}`}
                            title="Remove comment"
                          >
                            <BsTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="job-add-comment-add">
                    <input
                      type="text"
                      value={truckCommentDraft}
                      disabled={submitting}
                      placeholder="Enter a truck comment..."
                      onChange={(event) =>
                        setTruckCommentDraft(event.target.value)
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();

                          addTruckComment();
                        }
                      }}
                    />

                    <button
                      type="button"
                      className="job-btn ghost"
                      disabled={submitting || !truckCommentDraft.trim()}
                      onClick={addTruckComment}
                    >
                      <BsPlusLg />
                      Add Comment
                    </button>
                  </div>
                </section>
              </SectionCard>

              {/* ============================================
                  TRAILER
              ============================================= */}

              <SectionCard
                title="Trailer Details"
                description="Optional trailer information and axle sensor configuration."
                icon={<BsTruck />}
                defaultOpen
              >
                <div className="job-grid">
                  <div className="job-field formInput">
                    <label htmlFor="trailerMake">Make</label>

                    <input
                      id="trailerMake"
                      type="text"
                      name="make"
                      value={trailerDetails.make}
                      placeholder="Trailer make"
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerYear">Year</label>

                    <input
                      id="trailerYear"
                      type="number"
                      name="year"
                      min="1900"
                      step="1"
                      value={trailerDetails.year}
                      placeholder="Year"
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerModel">Model</label>

                    <input
                      id="trailerModel"
                      type="text"
                      name="model"
                      value={trailerDetails.model}
                      placeholder="Model"
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerBody">Body</label>

                    <input
                      id="trailerBody"
                      type="text"
                      name="body"
                      value={trailerDetails.body}
                      placeholder="Body"
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerRego">Rego</label>

                    <input
                      id="trailerRego"
                      type="text"
                      name="rego"
                      value={trailerDetails.rego}
                      placeholder="Registration"
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerVin">VIN</label>

                    <input
                      id="trailerVin"
                      type="text"
                      name="vin"
                      value={trailerDetails.vin}
                      placeholder="Vehicle identification number"
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    />
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerConfig">Trailer Config</label>

                    <select
                      id="trailerConfig"
                      name="trailerConfig"
                      value={trailerDetails.trailerConfig}
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    >
                      <option value="">Select configuration...</option>

                      {TRAILER_CONFIGS.map((config) => (
                        <option key={config} value={config}>
                          {config}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerASensorType">
                      Trailer A Sensor Type
                    </label>

                    <select
                      id="trailerASensorType"
                      name="trailerASensorType"
                      value={trailerDetails.trailerASensorType}
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    >
                      <option value="">Select sensor type...</option>

                      {AXLE_SENSOR_TYPE.map((axleType) => (
                        <option key={`trailer-a-${axleType}`} value={axleType}>
                          {axleType}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="job-field formInput">
                    <label htmlFor="trailerBSensorType">
                      Trailer B Sensor Type
                    </label>

                    <select
                      id="trailerBSensorType"
                      name="trailerBSensorType"
                      value={trailerDetails.trailerBSensorType}
                      onChange={handle(setTrailerDetails)}
                      disabled={submitting}
                    >
                      <option value="">Select sensor type...</option>

                      {AXLE_SENSOR_TYPE.map((axleType) => (
                        <option key={`trailer-b-${axleType}`} value={axleType}>
                          {axleType}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ==========================================
                    TRAILER COMMENTS
                =========================================== */}

                <section className="job-add-comments-section">
                  <div className="job-add-comments-header">
                    <div className="job-add-comments-icon">
                      <BsChatLeftText />
                    </div>

                    <div>
                      <h3>Trailer Comments</h3>

                      <p>
                        Add notes about the trailer configuration or
                        installation requirements.
                      </p>
                    </div>
                  </div>

                  {trailerDetails.comments.length > 0 && (
                    <div className="job-add-comments">
                      {trailerDetails.comments.map((comment, index) => (
                        <div key={comment.id} className="job-add-comment">
                          <span className="job-add-comment-number">
                            {index + 1}
                          </span>

                          <span className="job-add-comment-text">
                            {comment.text}
                          </span>

                          <button
                            type="button"
                            disabled={submitting}
                            onClick={() => removeTrailerComment(comment.id)}
                            aria-label={`Remove trailer comment ${index + 1}`}
                            title="Remove comment"
                          >
                            <BsTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="job-add-comment-add">
                    <input
                      type="text"
                      value={trailerCommentDraft}
                      disabled={submitting}
                      placeholder="Enter a trailer comment..."
                      onChange={(event) =>
                        setTrailerCommentDraft(event.target.value)
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();

                          addTrailerComment();
                        }
                      }}
                    />

                    <button
                      type="button"
                      className="job-btn ghost"
                      disabled={submitting || !trailerCommentDraft.trim()}
                      onClick={addTrailerComment}
                    >
                      <BsPlusLg />
                      Add Comment
                    </button>
                  </div>
                </section>
              </SectionCard>

              <SectionCard
                title="Job Photos"
                description="Add job photos for reference"
                icon={<BsImages />}
                defaultOpen
              >
                <JobPhotosSection
                  images={jobImages}
                  onChange={setJobImages}
                  disabled={submitting}
                />
              </SectionCard>

              {/* ============================================
                  ACTIONS
              ============================================= */}

              <div className="job-add-actions">
                <button
                  type="button"
                  className="job-btn ghost"
                  disabled={submitting}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="job-btn primary"
                  disabled={!isValid || submitting}
                >
                  {submitting
                    ? uploadProgress
                      ? `Uploading ${uploadProgress.completed}/${uploadProgress.total}...`
                      : "Creating Job..."
                    : "Create Job"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default JobSheetAdd;
